/**
 * Main-thread side of the render worker (SPEC §12.0). One worker per window hosts every chart and
 * the single md socket. This module:
 *
 * - starts the worker and answers its token requests (cookies/fetch live on the main thread);
 * - decides the renderer: `worker` (canvas transferred with `transferControlToOffscreen`) or
 *   `compat` (same `ChartView` code on the main thread, socket still in the worker) when WebGL2 on
 *   an OffscreenCanvas in a worker is unavailable, or when forced with `?chart=compat`;
 * - forwards input, resize and theme changes;
 * - republishes worker stats for the status bar (via refs, never React state for ticks) and the
 *   `window.__helmChartStats()` debug hook used by the e2e check;
 * - relays the worker's md socket to the main-thread widgets (`setMarketFeed` / `watch`), so a
 *   window holds one md connection shared by charts, DOM ladder, Time & Sales and Watchlist;
 * - toasts gateway notices the user should see (`MD_GAP`, `MD_SYMBOL_LIMIT`, session notices).
 */
import { api, watchResume } from "@helm/core";
import type { BarSpec } from "@helm/core/bars";
import { unpackBars } from "@helm/core/history";
import {
  NOTICE_MD_GAP,
  NOTICE_MD_SYMBOL_LIMIT,
  NOTICE_TOO_MANY_REQUESTS,
  wallNowMs,
  type MdStatus,
} from "@helm/core/md";
import {
  readThemeFromCss,
  OwnedLast,
  type ChartView,
  type FrameLoop,
  type LatencyTracker,
  type ChartCommand,
  type ChartInput,
  type ChartProbe,
  type ChartSnapshot,
  type DrawingEvent,
  type DrawingsState,
  type OverlayOptions,
  type ChartSettings,
  type CompareGeometry,
  type PaneGeometry,
  type IndicatorDetails,
  type LastDetails,
  type LastPlacementEvent,
  type MainHeader,
  type ThemeTokens,
  type TradeLine,
  type TradeLineEvent,
  type ViewEvent,
  type ViewSync,
} from "@helm/chart";
import type { AlertFire, ChartAlertRule } from "@helm/core/alerts";
import type { ChartIndicatorsState } from "@helm/core/indicators";
import type { CompareState } from "@helm/core";
import { showMarketNotice } from "../components/Toasts";
import { tradeGhostLabel } from "./tradeGhost";
import type {
  CacheUsageReport,
  DemoFeedOptions,
  HistoryProgress,
  MainToWorker,
  NoticeLevel,
  ReloadScope,
  RendererMode,
  WidgetInstrument,
  WidgetQuote,
  WorkerStats,
  WorkerToMain,
  BundledScript,
  PineScriptSource,
} from "./protocol";

declare const __HELM_VERSION__: string;

export type HostStats = WorkerStats & { renderer: RendererMode };

export type ChartNotice = { text: string; level: NoticeLevel; sticky: boolean };

export type ChartHandle = {
  /** This chart's key in the stats (`HostStats.charts`). */
  readonly id: string;
  /** Renderer startup failed; retry must attach a fresh canvas with the latest chart state. */
  onRendererError(cb: (error: string | null) => void): () => void;
  setSymbol(symbol: string | null): void;
  setSpec(spec: BarSpec): void;
  resize(cssW: number, cssH: number, dpr: number): void;
  input(ev: ChartInput): void;
  /** Display time zone for time labels (IANA, e.g. "America/Chicago"); null = browser zone. */
  setTimeZone(timeZone: string | null): void;
  /** Overlay switches: heatmap, key levels, stats box, position / order lines. */
  setOverlays(options: OverlayOptions): void;
  /** Read-only position / working-order lines (absolute ticks) for this chart. */
  setTradeLines(lines: TradeLine[]): void;
  /** Chart settings (ADR-0026): style, colours, status line, price scale, appearance. */
  setChartSettings(settings: ChartSettings): void;
  /** Compare / overlay symbols (owner 2026-09-28); the state lives in the chart-layout pane spec. */
  setCompare(state: CompareState): void;
  /** Compare legend rows for the DOM overlay (on change). Returns the unsubscribe function. */
  onCompareRows(cb: (g: CompareGeometry) => void): () => void;
  /** Mark this chart as the target of chart commands (palette "Chart: Reload history"). */
  focus(): void;
  /** SPEC-ADDENDA A1.4: evict + refetch this chart's history (see historyReload.ts). */
  reloadHistory(scope: ReloadScope): void;
  /** History notices for this chart (null clears). Returns the unsubscribe function. */
  onNotice(cb: (n: ChartNotice | null) => void): () => void;
  /** The history chip (A1.2 / A1.4): loading / reloading windows, or paused. Returns unsubscribe. */
  onHistoryProgress(cb: (p: HistoryProgress | null) => void): () => void;
  /**
   * Split charts (ChartPanel's sync bus): the user's crosshair / range / jumps on this chart.
   * Only a chart with a listener reports anything. Returns the unsubscribe function.
   */
  onView(cb: (ev: ViewEvent) => void): () => void;
  /** Split charts: follow another pane (never reported back through `onView`). */
  syncView(s: ViewSync): void;
  /** Indicators + panes (ADR-0018); the state lives in the chart-layout pane spec. */
  setIndicators(state: ChartIndicatorsState): void;
  /** User-requested displayed-value snapshot; null after removal, detach or timeout. */
  indicatorDetails(instanceId: string): Promise<IndicatorDetails | null>;
  setLastRowEnabled(enabled: boolean): void;
  /** Stale-feed label for the owned Last row (from the StaleBadge watch), sent on change only. */
  setLastStale(label: string | null): void;
  lastDetails(): Promise<LastDetails | null>;
  /** Pane / legend geometry for the DOM overlay (on change). Returns the unsubscribe function. */
  onPanes(cb: (g: PaneGeometry) => void): () => void;
  /** Drawing tools (ADR-0023): drawings, armed tool, magnet… for this chart (any subset). */
  setDrawings(state: Partial<DrawingsState>): void;
  /** Chart alerts (drawings / indicators) to evaluate on this chart's bars (replaces the set). */
  setAlertRules(rules: ChartAlertRule[]): void;
  /** Drawing tools: Esc (cancel / finish a path / deselect) or Enter (finish a path). */
  drawingCommand(cmd: "cancel" | "finish"): void;
  /** Drawing tools: commits, selection, cursor, tool done, edit. Returns the unsubscribe function. */
  onDrawingEvent(cb: (ev: DrawingEvent) => void): () => void;
  /** Chart trading: trade-line drags (on release) and × clicks, and the cursor over them. */
  onTradeLineEvent(cb: (ev: TradeLineEvent) => void): () => void;
  /**
   * Chart menu: what is under (x, y) (CSS px of the canvas) — region, price ticks, bar time,
   * drawing hit (made the selection when `select`). Null when the chart cannot answer.
   */
  probe(x: number, y: number, select: boolean): Promise<ChartProbe | null>;
  /** Chart menu: reset view, auto-fit the price scale, lock / unlock the vertical cursor line. */
  command(cmd: ChartCommand): void;
  /** Snapshot: this chart drawn without the crosshair, as RGBA rows (device px); null on failure. */
  snapshot(): Promise<ChartSnapshot | null>;
  detach(): void;
};

/** Worker round trips (probe / snapshot) that never answer resolve to null after this. */
const REQUEST_TIMEOUT_MS = 3000;

type CompatChart = { view: ChartView };

/** The main-thread ("compat") renderer: loaded on first use, retried after a failed load. */
type CompatRenderer = Pick<typeof import("@helm/chart"), "ChartView" | "FrameLoop" | "LatencyTracker">;
let compatRenderer: Promise<CompatRenderer> | null = null;
function loadCompatRenderer(): Promise<CompatRenderer> {
  return (compatRenderer ??= import("@helm/chart").catch((e: unknown) => {
    compatRenderer = null;
    throw e;
  }));
}

/**
 * Live WebGL contexts one worker may hold: Chromium keeps at most 4 active contexts per worker
 * (kMaxGLActiveContextsOnWorker) and silently loses the oldest when a 5th is created, which froze
 * a chart as soon as a window showed a 5th chart. Charts beyond the budget use the compat renderer
 * (same engine on the main thread; the md socket stays in the worker). See ADR-0016.
 */
export const WORKER_GL_BUDGET = 4;

/** Prints for one watched symbol (exchange time `tsMs`, aggressor 1 buy / 2 sell / 0 unknown). */
export type WidgetPrints = { price: Float64Array; size: Float64Array; aggr: Uint8Array; tsMs: Float64Array };

/** Receiver of the shared md socket's data for main-thread widgets (the market-data hub). */
export type MarketFeedSink = {
  status(status: MdStatus, detail: string | null): void;
  instrument(requested: string, info: WidgetInstrument): void;
  trades(requested: string, prints: WidgetPrints): void;
  quotes(quotes: readonly WidgetQuote[]): void;
  /** A gateway notice for a watched symbol (already shortened for display). */
  notice(requested: string, text: string): void;
};

const statsListeners = new Set<(s: HostStats) => void>();
let lastStats: HostStats | null = null;
const noticeListeners = new Map<string, (n: ChartNotice | null) => void>();
const progressListeners = new Map<string, (p: HistoryProgress | null) => void>();
/** Last chip per chart, replayed to a late subscriber. */
const lastProgress = new Map<string, HistoryProgress>();
const usageWaiters = new Map<number, (r: CacheUsageReport) => void>();
let usageSeq = 0;
/** Settings → Data cap (bytes), per device (A1.6; ADR-0009 default 1 GB). */
export const HISTORY_CAP_KEY = "helm.history.capBytes";

function publishProgress(id: string, p: HistoryProgress | null): void {
  if (p === null) lastProgress.delete(id);
  else lastProgress.set(id, p);
  progressListeners.get(id)?.(p);
}
const viewListeners = new Map<string, (ev: ViewEvent) => void>();
const paneListeners = new Map<string, (g: PaneGeometry) => void>();
const drawingListeners = new Map<string, (ev: DrawingEvent) => void>();
const tradeLineListeners = new Map<string, (ev: TradeLineEvent) => void>();
const compareListeners = new Map<string, (g: CompareGeometry) => void>();
const alertListeners = new Set<(chartId: string, fires: AlertFire[]) => void>();

function publishAlerts(id: string, fires: AlertFire[]): void {
  for (const l of alertListeners) l(id, fires);
}

/** Chart alerts that fired on any chart of this window (worker or compat). Returns the unsubscribe. */
export function onChartAlerts(cb: (chartId: string, fires: AlertFire[]) => void): () => void {
  alertListeners.add(cb);
  return () => {
    alertListeners.delete(cb);
  };
}
/** Last geometry per chart, replayed to a late subscriber. */
const lastPanes = new Map<string, PaneGeometry>();
const lastCompareRows = new Map<string, CompareGeometry>();

function publishCompareRows(id: string, g: CompareGeometry): void {
  lastCompareRows.set(id, g);
  compareListeners.get(id)?.(g);
}

function publishPanes(id: string, g: PaneGeometry): void {
  lastPanes.set(id, g);
  paneListeners.get(id)?.(g);
}

function clearLastPlacement(id: string): void {
  const geometry = lastPanes.get(id);
  if (geometry?.header?.active)
    publishPanes(id, {
      ...geometry,
      header: { ...geometry.header, active: false, details: null },
    });
}

/**
 * DEV preview only: `/?preview&chart=demo[&demoBars=N][&demoTradeMs=N][&demoEnd=ISO][&demoAnchor=1]` feeds the
 * charts synthetic bars and trades (demoFeed.ts) instead of the md socket, for visual review and
 * perf checks (`demoEnd`: a fixed history ending then, no live trades).
 */
function demoOptions(): DemoFeedOptions | undefined {
  if (!import.meta.env.DEV) return undefined;
  const q = new URLSearchParams(location.search);
  if (!q.has("preview") || q.get("chart") !== "demo") return undefined;
  const num = (k: string, d: number): number => {
    const v = Number(q.get(k));
    return Number.isFinite(v) && v > 0 ? v : d;
  };
  const h = q.get("demoHistory");
  const history = h === "suspect" || h === "gap" || h === "paused" || h === "loading" ? h : undefined;
  const endMs = Date.parse(q.get("demoEnd") ?? "");
  return {
    bars: Math.min(200_000, num("demoBars", 3000)),
    tradeMs: num("demoTradeMs", 250),
    ...(history ? { history } : {}),
    ...(Number.isFinite(endMs) ? { endMs } : {}),
    ...(q.get("demoAnchor") === "1" ? { anchor: true } : {}),
  };
}
/** Attached chart ids in focus order (last = most recently focused). */
const focusOrder: string[] = [];

function touchFocus(id: string): void {
  const i = focusOrder.indexOf(id);
  if (i >= 0) focusOrder.splice(i, 1);
  focusOrder.push(id);
}

/** Subscribe to ~4 Hz stats (latency, trade counts). Works before the host exists. */
export function onChartStats(cb: (s: HostStats) => void): () => void {
  statsListeners.add(cb);
  if (lastStats) cb(lastStats);
  return () => {
    statsListeners.delete(cb);
  };
}

declare global {
  interface Window {
    __helmChartStats?: () => HostStats | null;
  }
}
window.__helmChartStats = () => lastStats;

function tabId(): string {
  try {
    return sessionStorage.getItem("helm:tab-id") ?? crypto.randomUUID();
  } catch {
    return crypto.randomUUID();
  }
}

export function chartTheme(): ThemeTokens {
  return readThemeFromCss(document.documentElement);
}

class RenderHost {
  private readonly worker: Worker;
  private readonly ready: Promise<RendererMode>;
  private mode: RendererMode = "worker";
  private readonly compat = new Map<string, CompatChart>();
  private compatTracker: LatencyTracker | null = null;
  private compatLoop: FrameLoop | null = null;
  private compatFontRequested = false;
  private feed: MarketFeedSink | null = null;
  private lastMdStatus: { status: MdStatus; detail: string | null } | null = null;
  private seq = 0;
  /** Charts currently drawing in the worker (≤ WORKER_GL_BUDGET). */
  private workerCharts = 0;
  private reqSeq = 0;
  private readonly probeWaiters = new Map<number, (p: ChartProbe | null) => void>();
  private readonly detailWaiters = new Map<number, (p: IndicatorDetails | null) => void>();
  private readonly lastDetailWaiters = new Map<number, (p: LastDetails | null) => void>();
  private mdRevision = 0;
  private readonly lastPlacement = new Map<
    string,
    {
      selection: number;
      enabled: boolean;
      revision: number;
      available: boolean;
      mode: RendererMode | null;
      header: MainHeader | null;
    }
  >();
  private readonly lastOwners = new Map<string, OwnedLast>();
  private readonly snapWaiters = new Map<number, (s: ChartSnapshot | null) => void>();

  constructor() {
    this.worker = new Worker(new URL("./render.worker.ts", import.meta.url), {
      type: "module",
      name: "helm-render",
    });
    let resolveReady: (m: RendererMode) => void = () => {};
    this.ready = new Promise((r) => {
      resolveReady = r;
    });
    this.worker.onmessage = (ev: MessageEvent<WorkerToMain>) => {
      const msg = ev.data;
      switch (msg.t) {
        case "lastBegin": {
          this.lastOwners.get(msg.id)?.begin(msg.owner);
          const view = this.compat.get(msg.id)?.view;
          if (view?.last.begin(msg.owner) && view.syncLast()) this.compatLoop?.kick();
          break;
        }
        case "lastSnapshot": {
          const view = this.compat.get(msg.id)?.view;
          if (view?.last.receive(msg.value) && view.syncLast()) this.compatLoop?.kick();
          break;
        }
        case "ready": {
          const forced = new URLSearchParams(location.search).get("chart") === "compat";
          const canTransfer = "transferControlToOffscreen" in HTMLCanvasElement.prototype;
          this.mode = !forced && canTransfer && msg.offscreenWebgl2 ? "worker" : "compat";
          resolveReady(this.mode);
          break;
        }
        case "needToken":
          api
            .gatewayToken()
            .then((r) => {
              this.post({ t: "token", reqId: msg.reqId, token: r.token, error: null });
            })
            .catch((e: unknown) => {
              this.post({
                t: "token",
                reqId: msg.reqId,
                token: null,
                error: e instanceof Error ? e.message : "token",
              });
            });
          break;
        case "stats":
          this.publish(msg.stats);
          break;
        case "instrument":
          this.compat.get(msg.id)?.view.setInstrument(msg.meta);
          this.compatLoop?.kick();
          break;
        case "status":
          this.compat.get(msg.id)?.view.engine.setStatus(msg.text);
          this.compatLoop?.kick();
          break;
        case "trades": {
          const c = this.compat.get(msg.id);
          if (c && this.compatTracker && this.compatLoop) {
            this.compatTracker.offsetMs = msg.offsetMs;
            c.view.ingest(msg.cols);
            this.compatTracker.arrived(msg.cols);
            this.compatLoop.kick();
          }
          // Always ack (even for a detached chart): the worker paces its posts on these.
          this.post({ t: "tradesAck", id: msg.id });
          break;
        }
        case "history": {
          const c = this.compat.get(msg.id);
          const bars = unpackBars(msg.bars);
          if (c && bars) {
            c.view.setHistory(bars, msg.cutoffTs);
            this.compatLoop?.kick();
          }
          break;
        }
        case "resetBars":
          this.compat.get(msg.id)?.view.resetBars();
          this.compatLoop?.kick();
          break;
        case "compareInstrument":
          this.compat.get(msg.id)?.view.compare.setInstrument(msg.cid, msg.meta);
          this.compatLoop?.kick();
          break;
        case "compareHistory": {
          const c = this.compat.get(msg.id);
          const bars = unpackBars(msg.bars);
          if (c && bars) {
            c.view.compare.setHistory(msg.cid, bars, msg.cutoffTs);
            this.compatLoop?.kick();
          }
          break;
        }
        case "compareReset":
          this.compat.get(msg.id)?.view.compare.resetBars(msg.cid);
          this.compatLoop?.kick();
          break;
        case "compareGeometry":
          publishCompareRows(msg.id, msg.geometry);
          break;
        // Compat charts: the other symbols their Pine scripts request, loaded by the worker.
        case "pineFeedInstrument":
          this.compat.get(msg.id)?.view.feeds.setInstrument(msg.key, msg.meta);
          this.compatLoop?.kick();
          break;
        case "pineFeedHistory": {
          const c = this.compat.get(msg.id);
          const bars = unpackBars(msg.bars);
          if (c && bars) {
            c.view.feeds.setHistory(msg.key, bars, msg.cutoffTs);
            this.compatLoop?.kick();
          }
          break;
        }
        case "pineFeedReset":
          this.compat.get(msg.id)?.view.feeds.resetBars(msg.key);
          this.compatLoop?.kick();
          break;
        case "pineFeedFail":
          this.compat.get(msg.id)?.view.feeds.fail(msg.key, msg.text);
          this.compatLoop?.kick();
          break;
        case "clearBars":
          // The market-data source changed (ADR-0028 amendment): nothing of the old one stays.
          this.compat.get(msg.id)?.view.clearBars();
          this.compatLoop?.kick();
          break;
        case "book":
          this.compat.get(msg.id)?.view.engine.ingestBook(msg.book, msg.barOpenTs);
          this.compatLoop?.kick();
          break;
        case "notice": {
          const n = msg.text === null ? null : { text: msg.text, level: msg.level, sticky: msg.sticky };
          if (msg.id === null) for (const cb of noticeListeners.values()) cb(n);
          else noticeListeners.get(msg.id)?.(n);
          break;
        }
        case "historyProgress":
          publishProgress(msg.id, msg.progress);
          break;
        case "historyBands": {
          const c = this.compat.get(msg.id);
          if (c) {
            c.view.historyBands.setBands(msg.bands);
            c.view.engine.markDirty();
            this.compatLoop?.kick();
          }
          break;
        }
        case "cacheUsage": {
          const w = usageWaiters.get(msg.reqId);
          usageWaiters.delete(msg.reqId);
          w?.(msg.report);
          break;
        }
        case "error":
          console.error(`[chart ${msg.id}]`, msg.message);
          break;
        case "view":
          viewListeners.get(msg.id)?.(msg.ev);
          break;
        case "panes": {
          this.publishLastPanes(msg.id, msg.geometry, msg.placementRevision, msg.lastSelection);
          break;
        }
        case "lastPlacement":
          this.acceptLastPlacement(msg.id, msg.event);
          break;
        case "drawingEvent":
          drawingListeners.get(msg.id)?.(msg.ev);
          break;
        case "tradeLineEvent":
          tradeLineListeners.get(msg.id)?.(msg.ev);
          break;
        case "alertFired":
          publishAlerts(msg.id, msg.fires);
          break;
        case "indicatorDetails": {
          const w = this.detailWaiters.get(msg.reqId);
          this.detailWaiters.delete(msg.reqId);
          w?.(msg.details);
          break;
        }
        case "lastDetails": {
          const reply = this.lastDetailWaiters.get(msg.reqId);
          this.lastDetailWaiters.delete(msg.reqId);
          reply?.(msg.details);
          break;
        }
        case "probe": {
          const w = this.probeWaiters.get(msg.reqId);
          this.probeWaiters.delete(msg.reqId);
          w?.(msg.probe);
          break;
        }
        case "snapshot": {
          const w = this.snapWaiters.get(msg.reqId);
          this.snapWaiters.delete(msg.reqId);
          w?.(
            msg.pixels ? { width: msg.width, height: msg.height, pixels: new Uint8Array(msg.pixels) } : null,
          );
          break;
        }
        case "mdStatus":
          this.mdRevision++;
          this.lastMdStatus = { status: msg.status, detail: msg.detail };
          this.feed?.status(msg.status, msg.detail);
          break;
        case "mdInstrument":
          this.feed?.instrument(msg.requested, msg.info);
          break;
        case "mdTrades":
          this.feed?.trades(msg.requested, msg);
          break;
        case "mdBbo":
          this.feed?.quotes(msg.quotes);
          break;
        case "mdNotice":
          if (msg.requested !== null && msg.code !== NOTICE_MD_GAP)
            this.feed?.notice(msg.requested, msg.text);
          // Gaps, the symbol cap and session-wide notices are toasted, once per code + symbol.
          // Per-symbol refusals (busy → retried, unknown symbol, …) show in place only, so a
          // watchlist of 8 failing symbols is not 8 toasts.
          if (
            msg.code === NOTICE_MD_GAP ||
            msg.code === NOTICE_MD_SYMBOL_LIMIT ||
            (msg.requested === null && msg.code !== NOTICE_TOO_MANY_REQUESTS)
          ) {
            showMarketNotice(msg.code, msg.text, msg.contract ?? msg.requested ?? "");
          }
          break;
      }
    };
    this.worker.addEventListener("error", () => {
      // An ErrorEvent invalidates availability; it does not prove worker termination.
      for (const [id, placement] of this.lastPlacement)
        if (placement.mode === "worker") this.invalidateLastPlacement(id, placement.revision);
    });
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const demo = demoOptions();
    this.post({
      t: "init",
      baseUrl: `${proto}//${location.host}`,
      clientVersion: typeof __HELM_VERSION__ === "string" ? __HELM_VERSION__ : "dev",
      tabId: tabId(),
      ...(demo ? { demo } : {}),
    });
    // Settings → Data cap (per device), before the cache opens.
    const cap = storedHistoryCap();
    if (cap !== null) this.post({ t: "cacheCap", bytes: cap });
    // The history cache is per user (§6.8). Unknown user → the worker fetches every time.
    api
      .me()
      .then((me) => {
        this.post({ t: "user", userId: me?.user_id ?? null });
      })
      .catch(() => {
        this.post({ t: "user", userId: null });
      });
    const onVisibility = (): void => {
      const hidden = document.visibilityState === "hidden";
      this.post({ t: "visibility", hidden });
      this.compatTracker?.setPaused(hidden);
    };
    document.addEventListener("visibilitychange", onVisibility);
    onVisibility();
    // CB4: back from sleep / tab shown again / network back: probe the md socket now.
    watchResume(document, window, () => {
      this.post({ t: "resume" });
    });
    // Theme / visual-style changes (data-theme, data-style attributes) are re-read and pushed to the worker.
    new MutationObserver(() => {
      const theme = chartTheme();
      this.post({ t: "theme", theme });
      for (const c of this.compat.values()) c.view.engine.setTheme(theme);
      this.compatLoop?.kick();
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-style", "class", "style"],
    });
  }

  private post(msg: MainToWorker, transfer: Transferable[] = []): void {
    this.worker.postMessage(msg, transfer);
  }

  private publishLastPanes(id: string, geometry: PaneGeometry, revision: number, selection: number): void {
    const current = this.lastPlacement.get(id);
    const owned = current && revision === current.revision && selection === current.selection;
    if (owned) {
      const header = geometry.header ?? null;
      if (JSON.stringify(current.header) !== JSON.stringify(header)) current.available = false;
      current.header = header;
    }
    // Ordinary pane geometry remains immediate. Only the new Last acknowledgement is staged.
    publishPanes(
      id,
      geometry.header && (!owned || !current.enabled || !current.available)
        ? { ...geometry, header: { ...geometry.header, active: false, details: null } }
        : geometry,
    );
  }

  private invalidateLastPlacement(id: string, revision: number): void {
    const current = this.lastPlacement.get(id);
    if (!current || current.revision !== revision) return;
    current.revision++;
    current.available = false;
    current.header = null;
    clearLastPlacement(id);
    this.route(id, { t: "lastRow", id, enabled: current.enabled, revision: current.revision });
  }

  private acceptLastPlacement(id: string, event: LastPlacementEvent): void {
    const current = this.lastPlacement.get(id);
    if (!current || current.revision !== event.revision) return;
    if (event.kind === "unavailable") {
      this.invalidateLastPlacement(id, event.revision);
      return;
    }
    if (
      current.selection !== event.selection ||
      JSON.stringify(current.header) !== JSON.stringify(event.header)
    )
      return;
    current.available = event.header.active;
    const geometry = lastPanes.get(id);
    if (geometry)
      publishPanes(id, {
        ...geometry,
        header:
          current.enabled && current.available
            ? event.header
            : { ...event.header, active: false, details: null },
      });
  }

  /** Route the shared md socket's widget data to `sink` (null detaches). */
  setMarketFeed(sink: MarketFeedSink | null): void {
    this.feed = sink;
    if (sink && this.lastMdStatus) sink.status(this.lastMdStatus.status, this.lastMdStatus.detail);
  }

  /** Main-thread widgets start/stop needing `symbol` (the caller ref-counts). */
  watch(symbol: string, on: boolean): void {
    this.post({ t: "watch", symbol, on });
  }

  private publish(ws: WorkerStats): void {
    const stats: HostStats = { ...ws, renderer: this.mode };
    if (this.compatTracker) {
      // Compat charts: all of them in compat mode, or those over the worker's WebGL budget.
      for (const [id, c] of this.compat) stats.charts[id] = c.view.stats();
      if (this.mode === "compat") {
        stats.mode = "compat";
        stats.latency = this.compatTracker.snapshot();
      }
    }
    lastStats = stats;
    for (const l of statsListeners) l(stats);
  }

  /**
   * Attach a chart to a fresh `<canvas>` element (it is transferred in worker mode, so callers
   * must not reuse it). Returns a handle immediately; messages queue until the worker is ready.
   */
  attach(
    canvas: HTMLCanvasElement,
    symbol: string | null,
    spec: BarSpec,
    cssW: number,
    cssH: number,
  ): ChartHandle {
    const id = `c${String(++this.seq)}`;
    this.lastPlacement.set(id, {
      selection: 0,
      enabled: false,
      revision: 0,
      available: false,
      mode: null,
      header: null,
    });
    const lastOwner = new OwnedLast();
    lastOwner.select(id, 0);
    this.lastOwners.set(id, lastOwner);
    let detached = false;
    let failed: string | null = null;
    let errorListener: ((error: string | null) => void) | null = null;
    const pending: MainToWorker[] = [];
    let mode: RendererMode | null = null;
    let inWorker = false;
    const send = (m: MainToWorker): void => {
      if (detached || failed) return;
      if (mode === null) pending.push(m);
      else this.route(id, m);
    };
    const flush = (m: RendererMode): void => {
      mode = m;
      const placement = this.lastPlacement.get(id);
      if (placement) placement.mode = m;
      for (const p of pending) this.route(id, p);
      pending.length = 0;
    };
    const fail = (error: unknown): void => {
      if (detached) return;
      console.error("[chart] renderer failed", error);
      failed = "Couldn't load the chart. Retry when your connection is available.";
      this.lastPlacement.delete(id);
      this.lastOwners.delete(id);
      pending.length = 0;
      clearLastPlacement(id);
      errorListener?.(failed);
    };
    void this.ready
      .then((m) => {
        if (detached) return;
        const dpr = window.devicePixelRatio || 1;
        const theme = chartTheme();
        // Over the worker's WebGL budget this chart renders on the main thread instead.
        if (m === "worker" && this.workerCharts < WORKER_GL_BUDGET) {
          inWorker = true;
          this.workerCharts++;
          const off = canvas.transferControlToOffscreen();
          this.post(
            {
              t: "attach",
              id,
              mode: "worker",
              canvas: off,
              symbol,
              spec,
              cssW,
              cssH,
              dpr,
              theme,
              lastSelection: 0,
            },
            [off],
          );
          flush("worker");
          return;
        }
        // The main-thread renderer is its own chunk (audit S-12): most windows never need it.
        // Messages keep queueing until it is loaded.
        return loadCompatRenderer().then(
          (lib) => {
            if (detached) return;
            this.startCompat(lib);
            this.loadCompatFont(theme.font);
            try {
              const view = new lib.ChartView(
                canvas,
                { cssWidth: cssW, cssHeight: cssH, dpr, theme },
                symbol ?? "",
                spec,
                () => wallNowMs() + (this.compatTracker?.offsetMs ?? 0),
              );
              this.compat.set(id, { view });
              view.last.select(id, 0);
              this.compatLoop?.add(view);
              view.onLastPlacement((event) => {
                this.acceptLastPlacement(id, event);
              });
              view.indicators.onGeometry((g) => {
                this.publishLastPanes(id, g, view.stageLastPlacement(g.header), view.last.selectionRevision);
              });
              view.drawings.onEvent((ev) => {
                drawingListeners.get(id)?.(ev);
              });
              view.engine.onTradeLineEvent((ev) => {
                tradeLineListeners.get(id)?.(ev);
              });
              view.engine.setGhostLabeller(tradeGhostLabel);
              view.compare.onGeometry((g) => {
                publishCompareRows(id, g);
              });
              view.feeds.onWant((wanted) => {
                this.post({ t: "pineFeeds", id, wanted });
              });
              view.onAlerts((fires) => {
                publishAlerts(id, fires);
              });
            } catch (e) {
              const chart = this.compat.get(id);
              if (chart) {
                this.compatLoop?.delete(chart.view);
                chart.view.dispose();
                this.compat.delete(id);
              }
              fail(e);
              return;
            }
            this.post({ t: "attach", id, mode: "compat", symbol, spec, lastSelection: 0 });
            flush("compat");
          },
          (e: unknown) => {
            fail(e);
          },
        );
      })
      .catch(fail);
    // Skip no-op changes: a symbol/spec message resets the chart's builder.
    let curSymbol = symbol;
    let lastSelection = 0;
    let curSpec = JSON.stringify(spec);
    let curZone: string | null = null;
    let curOverlays = "";
    let curLines = "";
    let curSettings = "";
    let curCompare = "";
    touchFocus(id);
    return {
      id,
      onRendererError: (cb) => {
        errorListener = cb;
        cb(failed);
        return () => {
          if (errorListener === cb) errorListener = null;
        };
      },
      setSymbol: (s) => {
        if (s === curSymbol) return;
        curSymbol = s;
        lastOwner.select(id, lastSelection + 1);
        const placement = this.lastPlacement.get(id);
        if (placement) {
          placement.selection = lastSelection + 1;
          placement.available = false;
          placement.header = null;
        }
        clearLastPlacement(id);
        send({ t: "symbol", id, symbol: s, lastSelection: ++lastSelection });
      },
      setSpec: (sp) => {
        const key = JSON.stringify(sp);
        if (key === curSpec) return;
        curSpec = key;
        send({ t: "spec", id, spec: sp });
      },
      resize: (w, h, dpr) => {
        send({ t: "resize", id, cssW: w, cssH: h, dpr });
      },
      input: (ev) => {
        send({ t: "input", id, ev });
      },
      setTimeZone: (tz) => {
        if (tz === curZone) return;
        curZone = tz;
        send({ t: "timeZone", id, timeZone: tz });
      },
      setOverlays: (options) => {
        const key = JSON.stringify(options);
        if (key === curOverlays) return;
        curOverlays = key;
        send({ t: "overlays", id, options });
      },
      setChartSettings: (settings) => {
        const key = JSON.stringify(settings);
        if (key === curSettings) return;
        curSettings = key;
        send({ t: "chartSettings", id, settings });
      },
      setCompare: (state) => {
        const key = JSON.stringify(state);
        if (key === curCompare) return;
        curCompare = key;
        send({ t: "compare", id, state });
      },
      onCompareRows: (cb) => {
        compareListeners.set(id, cb);
        const last = lastCompareRows.get(id);
        if (last) cb(last);
        return () => {
          if (compareListeners.get(id) === cb) compareListeners.delete(id);
        };
      },
      setTradeLines: (lines) => {
        const key = JSON.stringify(lines);
        if (key === curLines) return;
        curLines = key;
        send({ t: "tradeLines", id, lines });
      },
      focus: () => {
        touchFocus(id);
      },
      reloadHistory: (scope) => {
        // A compat chart measures its visible span here; a worker chart's is known in the worker.
        const range = scope === "visible" ? this.compat.get(id)?.view.historyBands.visibleTime : null;
        send(range ? { t: "reloadHistory", id, scope, range } : { t: "reloadHistory", id, scope });
      },
      onNotice: (cb) => {
        noticeListeners.set(id, cb);
        return () => {
          if (noticeListeners.get(id) === cb) noticeListeners.delete(id);
        };
      },
      onHistoryProgress: (cb) => {
        progressListeners.set(id, cb);
        cb(lastProgress.get(id) ?? null);
        return () => {
          if (progressListeners.get(id) === cb) progressListeners.delete(id);
        };
      },
      onView: (cb) => {
        viewListeners.set(id, cb);
        send({ t: "viewEvents", id, on: true });
        return () => {
          if (viewListeners.get(id) !== cb) return;
          viewListeners.delete(id);
          if (!detached) send({ t: "viewEvents", id, on: false });
        };
      },
      syncView: (s) => {
        send({ t: "viewSync", id, sync: s });
      },
      setIndicators: (state) => {
        send({ t: "indicators", id, state });
      },
      setDrawings: (state) => {
        send({ t: "drawings", id, state });
      },
      setAlertRules: (rules) => {
        send({ t: "alertRules", id, rules });
      },
      drawingCommand: (cmd) => {
        send({ t: "drawingCmd", id, cmd });
      },
      onDrawingEvent: (cb) => {
        drawingListeners.set(id, cb);
        return () => {
          if (drawingListeners.get(id) === cb) drawingListeners.delete(id);
        };
      },
      onTradeLineEvent: (cb) => {
        tradeLineListeners.set(id, cb);
        return () => {
          if (tradeLineListeners.get(id) === cb) tradeLineListeners.delete(id);
        };
      },
      indicatorDetails: (instanceId) =>
        detached || failed
          ? Promise.resolve(null)
          : this.request(this.detailWaiters, (reqId) => {
              send({ t: "indicatorDetails", id, reqId, instanceId });
            }).then((details) => (detached || failed ? null : details)),
      setLastRowEnabled: (enabled) => {
        const placement = this.lastPlacement.get(id);
        if (!placement) return;
        if (placement.enabled !== enabled) {
          placement.revision++;
          placement.available = false;
          placement.header = null;
          clearLastPlacement(id);
        }
        placement.enabled = enabled;
        send({ t: "lastRow", id, enabled, revision: placement.revision });
      },
      setLastStale: (label) => {
        send({ t: "lastStale", id, label });
      },
      lastDetails: () => {
        if (detached || failed) return Promise.resolve(null);
        const selection = lastSelection;
        const mdRevision = this.mdRevision;
        return this.request(this.lastDetailWaiters, (reqId) => {
          send({ t: "lastDetails", id, reqId, selection });
        }).then((details) =>
          detached ||
          failed ||
          selection !== lastSelection ||
          mdRevision !== this.mdRevision ||
          JSON.stringify(details?.owner) !== JSON.stringify(this.lastOwners.get(id)?.snapshot?.owner) ||
          details?.owner?.chartId !== id ||
          details.owner.selection !== selection
            ? null
            : details,
        );
      },
      probe: (x, y, select) =>
        detached || failed
          ? Promise.resolve(null)
          : this.request(this.probeWaiters, (reqId) => {
              send({ t: "probe", id, reqId, x, y, select });
            }),
      command: (cmd) => {
        send({ t: "chartCmd", id, cmd });
      },
      snapshot: () =>
        detached || failed
          ? Promise.resolve(null)
          : this.request(this.snapWaiters, (reqId) => {
              send({ t: "snapshot", id, reqId });
            }),
      onPanes: (cb) => {
        paneListeners.set(id, cb);
        const last = lastPanes.get(id);
        if (last) cb(last);
        return () => {
          if (paneListeners.get(id) === cb) paneListeners.delete(id);
        };
      },
      detach: () => {
        if (detached) return;
        detached = true;
        this.lastPlacement.delete(id);
        this.lastOwners.delete(id);
        clearLastPlacement(id);
        pending.length = 0;
        errorListener = null;
        noticeListeners.delete(id);
        progressListeners.delete(id);
        lastProgress.delete(id);
        viewListeners.delete(id);
        paneListeners.delete(id);
        drawingListeners.delete(id);
        tradeLineListeners.delete(id);
        compareListeners.delete(id);
        lastCompareRows.delete(id);
        lastPanes.delete(id);
        const i = focusOrder.indexOf(id);
        if (i >= 0) focusOrder.splice(i, 1);
        if (inWorker) this.workerCharts--;
        if (mode !== null) this.route(id, { t: "detach", id });
      },
    };
  }

  /** One worker round trip; resolves null when no answer comes within `REQUEST_TIMEOUT_MS`. */
  private request<T>(
    waiters: Map<number, (v: T | null) => void>,
    ask: (reqId: number) => void,
  ): Promise<T | null> {
    const reqId = ++this.reqSeq;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (waiters.delete(reqId)) resolve(null);
      }, REQUEST_TIMEOUT_MS);
      waiters.set(reqId, (v) => {
        clearTimeout(timer);
        resolve(v);
      });
      ask(reqId);
    });
  }

  /** Palette / Alt+R: reset the view of the most recently focused chart. */
  resetView(): void {
    const id = focusOrder.at(-1);
    if (id !== undefined) this.route(id, { t: "chartCmd", id, cmd: { kind: "resetView" } });
  }

  /** Palette: reload the most recently focused chart (or every chart when none is attached). */
  reloadHistory(scope: ReloadScope): void {
    this.post({ t: "reloadHistory", id: focusOrder.at(-1) ?? null, scope });
  }

  clearHistoryCache(): void {
    this.post({ t: "clearHistoryCache" });
  }

  /** Settings → Data (A1.6): this user's cache usage per symbol, for the current data source. */
  historyCacheUsage(): Promise<CacheUsageReport> {
    const reqId = ++usageSeq;
    return new Promise((resolve) => {
      usageWaiters.set(reqId, resolve);
      this.post({ t: "cacheUsage", reqId });
    });
  }

  /** Settings → Data "Clear": one contract, or all of this user's cached history when null. */
  clearHistorySymbol(symbol: string | null): void {
    this.post({ t: "cacheClear", symbol });
  }

  /** Settings → Data "Reload": charts on the contract reload every timeframe. */
  reloadHistorySymbol(symbol: string): void {
    this.post({ t: "cacheReload", symbol });
  }

  /** Settings → Data: the device cap (A1.6), remembered per device. */
  setHistoryCap(bytes: number): void {
    try {
      localStorage.setItem(HISTORY_CAP_KEY, String(bytes));
    } catch {
      // Storage blocked: the cap still applies for this session.
    }
    this.post({ t: "cacheCap", bytes });
  }

  /**
   * The user's Pine scripts (SPEC-ADDENDA A6): the worker compiles them; compat charts share the
   * main-thread registry (already updated by the caller) and only need to rebuild.
   */
  setPineScripts(scripts: PineScriptSource[]): void {
    this.post({ t: "pineScripts", scripts });
    for (const c of this.compat.values()) {
      c.view.indicators.setState(c.view.indicators.current);
      c.view.alerts.refreshDefinitions();
    }
    this.compatLoop?.kick();
  }

  /**
   * The bundled Pine indicators (chart/pine/bundled.ts): the worker compiles them; compat charts
   * share the main-thread registry (already updated by the caller) and only need to rebuild.
   */
  setBundledPine(scripts: BundledScript[]): void {
    this.post({ t: "bundledPine", scripts });
    for (const c of this.compat.values()) {
      c.view.indicators.setState(c.view.indicators.current);
      c.view.alerts.refreshDefinitions();
    }
    this.compatLoop?.kick();
  }

  /** DEV-ONLY `/?preview`: charts run on the worker's synthetic feed instead of the md socket. */
  devFeed(): void {
    if (import.meta.env.DEV) this.post({ t: "devFeed" });
  }

  /** Worker mode: everything goes to the worker. Compat: view-local messages stay here. */
  private route(id: string, m: MainToWorker): void {
    const c = this.compat.get(id);
    if (c) {
      switch (m.t) {
        case "resize":
          c.view.engine.resize(m.cssW, m.cssH, m.dpr);
          this.compatLoop?.kick();
          return;
        case "input":
          c.view.input(m.ev);
          this.compatLoop?.kick();
          return;
        case "indicators":
          c.view.indicators.setState(m.state);
          this.compatLoop?.kick();
          return;
        case "drawings":
          c.view.drawings.setState(m.state);
          this.compatLoop?.kick();
          return;
        case "alertRules":
          c.view.setAlertRules(m.rules);
          return;
        case "drawingCmd":
          if (m.cmd === "cancel") c.view.drawings.cancel();
          else c.view.drawings.finishMulti();
          this.compatLoop?.kick();
          return;
        case "timeZone":
          c.view.engine.setTimeZone(m.timeZone);
          this.compatLoop?.kick();
          return;
        case "viewEvents":
          c.view.engine.setViewListener(
            m.on
              ? (ev) => {
                  viewListeners.get(id)?.(ev);
                }
              : null,
          );
          return;
        case "viewSync":
          c.view.engine.applyViewSync(m.sync);
          this.compatLoop?.kick();
          return;
        case "overlays":
          c.view.engine.setOverlayOptions(m.options);
          this.compatLoop?.kick();
          return;
        case "indicatorDetails": {
          const w = this.detailWaiters.get(m.reqId);
          this.detailWaiters.delete(m.reqId);
          w?.(c.view.indicators.detailSnapshot(m.instanceId));
          return;
        }
        case "lastRow":
          c.view.setLastRowEnabled(m.enabled, m.revision);
          this.compatLoop?.kick();
          return;
        case "lastStale":
          if (c.view.setLastStale(m.label)) this.compatLoop?.kick();
          return;
        case "lastDetails": {
          const reply = this.lastDetailWaiters.get(m.reqId);
          this.lastDetailWaiters.delete(m.reqId);
          reply?.(c.view.lastDetails(m.selection));
          return;
        }
        case "probe": {
          const w = this.probeWaiters.get(m.reqId);
          this.probeWaiters.delete(m.reqId);
          w?.(c.view.probe(m.x, m.y, m.select));
          this.compatLoop?.kick();
          return;
        }
        case "chartCmd":
          c.view.command(m.cmd);
          this.compatLoop?.kick();
          return;
        case "snapshot": {
          const w = this.snapWaiters.get(m.reqId);
          this.snapWaiters.delete(m.reqId);
          w?.(c.view.engine.snapshot(performance.now()));
          this.compatLoop?.kick();
          return;
        }
        case "chartSettings":
          c.view.engine.setChartSettings(m.settings);
          this.compatLoop?.kick();
          return;
        case "compare":
          c.view.compare.setState(m.state);
          this.compatLoop?.kick();
          this.post(m); // the worker subscribes the symbols and loads their history
          return;
        case "tradeLines":
          c.view.engine.setTradeLines(m.lines);
          this.compatLoop?.kick();
          return;
        case "spec":
          c.view.setSpec(m.spec);
          this.compatLoop?.kick();
          this.post(m); // the worker's history loader follows the spec
          return;
        case "symbol":
          c.view.last.select(id, m.lastSelection);
          c.view.setSymbol(m.symbol ?? "");
          this.compatLoop?.kick();
          this.post(m);
          return;
        case "detach":
          this.compatLoop?.delete(c.view);
          c.view.dispose();
          this.compat.delete(id);
          this.post(m);
          return;
        default:
          break;
      }
    }
    this.post(m);
  }

  /**
   * Compat charts rasterise text on the main thread, where `--font-num` is a CSS web font that
   * loads lazily: load it, then re-rasterise every compat chart's atlas once.
   */
  private loadCompatFont(font: string | undefined): void {
    if (this.compatFontRequested || !font || !("fonts" in document)) return;
    this.compatFontRequested = true;
    document.fonts
      .load(`11px ${font}`)
      .then(() => {
        for (const c of this.compat.values()) c.view.engine.refreshFont();
        this.compatLoop?.kick();
      })
      .catch(() => undefined);
  }

  private startCompat(lib: CompatRenderer): void {
    if (this.compatTracker) return;
    this.compatTracker = new lib.LatencyTracker();
    this.compatTracker.setPaused(document.visibilityState === "hidden");
    this.compatLoop = new lib.FrameLoop(this.compatTracker);
  }
}

/** The cap chosen on the Settings → Data page (bytes), or null for the default. */
export function storedHistoryCap(): number | null {
  try {
    const v = Number(localStorage.getItem(HISTORY_CAP_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

let host: RenderHost | null = null;

export function renderHost(): RenderHost {
  host ??= new RenderHost();
  return host;
}

/** Charts currently attached in this window (palette `when`). */
export function chartCount(): number {
  return focusOrder.length;
}
